import React from "react";
import { Apple } from "lucide-react";

/**
 * SocialSignInButtons · Director directive 2026.02 (Strike #19)
 *
 * Drop-in row of social auth buttons for `/login` and `/bridge`
 * GhostBridge. Uses the Emergent-managed Google Auth flow:
 *   - Click → redirect to `https://auth.emergentagent.com/?redirect=...`
 *   - Emergent handles Google OAuth + consent screen
 *   - User lands back at `<origin>/auth/callback#session_id=...`
 *   - AuthCallback.js redeems the session_id
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 *
 * Apple button is rendered DISABLED with a "Coming soon" hint until
 * the Director provisions Apple Developer credentials (Team ID, Key
 * ID, Services ID, .p8 private key). Rendering it greyed-out (vs
 * hiding it) signals to operators that the channel is intentional
 * and arriving.
 */
export default function SocialSignInButtons({ testidPrefix = "social-signin" }) {
    const onGoogle = () => {
        // Use the browser's current origin so the redirect lands back on
        // the exact host the user signed in from — works on canonical
        // (eigooinc.net), preview, AND any future subdomain.
        const redirectUrl = `${window.location.origin}/auth/callback`;
        window.location.href =
            "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
    };

    return (
        <div className="flex flex-col gap-2" data-testid={`${testidPrefix}-row`}>
            <div
                className="text-[10px] uppercase tracking-[0.3em] flex items-center gap-2 mb-1"
                style={{
                    color: "#00E5FF",
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 700,
                }}
            >
                <span aria-hidden style={{ opacity: 0.6 }}>◢</span>
                Or board with a social signal
            </div>

            {/* Google · Sovereign-managed */}
            <button
                type="button"
                onClick={onGoogle}
                data-testid={`${testidPrefix}-google`}
                className="inline-flex items-center justify-center gap-3 w-full px-5 py-3 uppercase tracking-[0.22em] text-xs font-bold transition-all duration-300"
                style={{
                    fontFamily: "'Montserrat', sans-serif",
                    background: "#F8FAFC",
                    color: "#050A0F",
                    border: "1px solid #F8FAFC",
                    minHeight: 48,
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 0 20px rgba(255,255,255,0.35)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "none";
                    e.currentTarget.style.transform = "translateY(0)";
                }}
            >
                <GoogleGlyph size={16} />
                Continue with Google
            </button>

            {/* Apple · disabled until Apple Developer credentials arrive */}
            <button
                type="button"
                disabled
                data-testid={`${testidPrefix}-apple-disabled`}
                title="Pending Apple Developer credentials — provisioning queued."
                className="inline-flex items-center justify-center gap-3 w-full px-5 py-3 uppercase tracking-[0.22em] text-xs font-bold transition-all duration-300 cursor-not-allowed"
                style={{
                    fontFamily: "'Montserrat', sans-serif",
                    background: "rgba(248, 250, 252, 0.06)",
                    color: "#5C7894",
                    border: "1px solid #1E3654",
                    minHeight: 48,
                }}
            >
                <Apple size={16} strokeWidth={1.8} />
                Continue with Apple
                <span
                    className="text-[9px] uppercase tracking-[0.3em] ml-1 px-1.5 py-0.5"
                    style={{
                        color: "#E8C56C",
                        border: "1px solid #E8C56C55",
                        background: "rgba(232,197,108,0.06)",
                    }}
                >
                    Soon
                </span>
            </button>
        </div>
    );
}

// Inline Google glyph (no external dependency, no AI-slop emoji).
function GoogleGlyph({ size = 16 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden>
            <path
                fill="#FFC107"
                d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
            />
            <path
                fill="#FF3D00"
                d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
            />
            <path
                fill="#4CAF50"
                d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
            />
            <path
                fill="#1976D2"
                d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
            />
        </svg>
    );
}
